import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { getExamById, groupExamItems, saveExamAnswer, saveExamCurrentPosition, pauseExamTimerOnUnload, finishExam, resumeExamTimer, reportQuestion, completeStudyPlanTask } from 'utils/supabase';
import { consumeStudyPlanTaskLink } from 'utils/studyPlanLink';
import { getChallengeInviteCode } from 'utils/challengeLink';
import BackdropAlert from "components/Custom/BackdropAlert.js";

// components

import { Chip, Stack, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { ScrollText, Stethoscope, ArrowRight, ArrowLeft, Flag } from 'lucide-react';

function getDifficultyLabel(value) {
  const normalized = String(value || '').trim().toLowerCase();

  if (['easy', 'facil', 'fácil'].includes(normalized)) {
    return { label: 'Fácil', color: '#15803d', border: '#86efac' };
  }

  if (['hard', 'difficult', 'dificil', 'difícil'].includes(normalized)) {
    return { label: 'Difícil', color: '#dc2626', border: '#fca5a5' };
  }

  return { label: 'Media', color: '#b45309', border: '#fcd34d' };
}

export default function Simulador() {
  const [preguntas, setPreguntas] = useState(null);
  const [bloquActual, setBloquActual] = useState(0);
  const [respuestasSeleccionadas, setRespuestasSeleccionadas] = useState({});
  const [preguntasMarcadas, setPreguntasMarcadas] = useState({});
  const [hoverOption, setHoverOption] = useState(null);
  const [timerInfo, setTimerInfo] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerMode, setTimerMode] = useState('stopwatch');
  const [isFinishing, setIsFinishing] = useState(false);
  const [isLoadingExam, setIsLoadingExam] = useState(true);
  const [paginaNavegador, setPaginaNavegador] = useState(0);
  const [brokenImagesByBlock, setBrokenImagesByBlock] = useState({});
  const [imageAspectRatioByBlock, setImageAspectRatioByBlock] = useState({});
  const [reportTarget, setReportTarget] = useState(null);
  const [reportComment, setReportComment] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [preguntasReportadas, setPreguntasReportadas] = useState({});
  const [backdropAlert, setBackdropAlert] = useState({
    open: false,
    severity: "info",
    title: "",
    message: "",
    description: "",
    closeOnBackdropClick: true,
    primaryAction: null,
    secondaryAction: null,
  });
  const { examId } = useParams();
  const history = useHistory();

  const preguntasRef = useRef(null);
  const finishingRef = useRef(false);

  const showBackdropAlert = useCallback(({
    severity = "info",
    title = "",
    message = "",
    description = "",
    closeOnBackdropClick = true,
    primaryAction = null,
    secondaryAction = null,
  }) => {
    setBackdropAlert({
      open: true,
      severity,
      title,
      message,
      description,
      closeOnBackdropClick,
      primaryAction,
      secondaryAction,
    });
  }, []);

  const closeBackdropAlert = useCallback(() => {
    setBackdropAlert((prev) => ({ ...prev, open: false }));
  }, []);

const handleFinishExam = useCallback(async (autoFinished = false) => {
  if (finishingRef.current) return;

  try {
    finishingRef.current = true;
    setIsFinishing(true);

    await finishExam(examId);

    const linkedTaskId = consumeStudyPlanTaskLink("exam", examId);
    if (linkedTaskId) {
      const { error: completeError } = await completeStudyPlanTask(linkedTaskId, { exam_id: examId });
      if (completeError) console.error("Error completing study plan task (exam):", completeError);
    }

    // No se borra el vinculo: se sigue necesitando para reabrir el mismo reto desde Resultados.
    const challengeInviteCode = getChallengeInviteCode(examId);
    const resultsPath = challengeInviteCode
      ? `/admin/reto/${challengeInviteCode}/resultados`
      : `/admin/simulador/${examId}/resultados`;

    if (autoFinished) {
      showBackdropAlert({
        severity: "warning",
        title: "Tiempo terminado",
        message: "Se terminó el tiempo del examen.",
        description: "Pulsa Aceptar para ver tus resultados.",
        closeOnBackdropClick: false,
        primaryAction: {
          label: "Aceptar",
          onClick: () => {
            closeBackdropAlert();
            history.push(resultsPath);
          },
        },
      });
      return;
    }

    // React Router v5
    history.push(resultsPath);

    // React Router v6:
    // navigate(`/exam/${examId}/results`);
  } catch (error) {
    console.error('Error finishing exam:', error);
    showBackdropAlert({
      severity: "error",
      title: "No se pudo finalizar",
      message: "No se pudo finalizar el examen.",
      description: "Por favor intenta nuevamente.",
      primaryAction: {
        label: "Aceptar",
        onClick: closeBackdropAlert,
      },
    });
    finishingRef.current = false;
  } finally {
    setIsFinishing(false);
  }
}, [examId, history, showBackdropAlert, closeBackdropAlert]);

  useEffect(() => {
    async function loadExam() {
      setIsLoadingExam(true);

      try {

        const { exam, items, answers } = await getExamById(examId);

        const examStatus = String(exam?.status || "").toLowerCase();

        if (examStatus === "completed") {
          const challengeInviteCode = getChallengeInviteCode(examId);
          const resultsPath = challengeInviteCode
            ? `/admin/reto/${challengeInviteCode}/resultados`
            : `/admin/simulador/${examId}/resultados`;

          showBackdropAlert({
            severity: "info",
            title: "Examen finalizado",
            message: "Este examen ya fue completado.",
            description: "Pulsa Ver resultados para continuar.",
            closeOnBackdropClick: false,
            primaryAction: {
              label: "Ver resultados",
              onClick: () => {
                closeBackdropAlert();
                history.push(resultsPath);
              },
            },
          });
          return;
        }

        const timer = await resumeExamTimer(examId);

        setTimerInfo(timer);

        const groupedQuestions = groupExamItems(items);
        setPreguntas(groupedQuestions);

        const answersMap = {};

        answers.forEach((answer) => {
          answersMap[answer.question_id] = answer.selected_option_id;
        });

        setRespuestasSeleccionadas(answersMap);
        const savedPosition = exam.settings?.current_position;
        const maxBlockIndex = Math.max(0, groupedQuestions.length - 1);

        // Si el usuario aun no respondio nada en este examen (ej. un reto recien aceptado),
        // ignoramos settings.current_position porque puede venir heredado de otro intento.
        if (answers.length > 0 && savedPosition !== null && savedPosition !== undefined) {
          setBloquActual(Math.min(Math.max(0, savedPosition), maxBlockIndex));
        }
        else {
          // Buscar la última pregunta contestada
          if (answers.length > 0) {
            const lastAnsweredQuestion = answers[answers.length - 1];
            const blockIndex = groupedQuestions.findIndex(block =>
              block.questions.some(q => q.question_id === lastAnsweredQuestion.question_id)
            );
            setBloquActual(blockIndex >= 0 ? blockIndex : 0);
          } else {
            setBloquActual(0);
          }
        }
      } catch (error) {
        console.error('Error loading exam:', error);
        showBackdropAlert({
          severity: "error",
          title: "Error al cargar",
          message: "No se pudo cargar el examen.",
          description: "Intenta de nuevo en unos segundos.",
        });
      } finally {
        setIsLoadingExam(false);
      }
    }

    loadExam();
  }, [examId, showBackdropAlert, closeBackdropAlert, history]);

  useEffect(() => {
    if (!timerInfo) return;

    const serverNowMs = new Date(timerInfo.server_now).getTime();
    const serverOffset = Date.now() - serverNowMs;

    const baseAccumulatedSeconds = timerInfo.timer_accumulated_seconds || 0;
    const runningStartedAtMs = timerInfo.timer_started_at
      ? new Date(timerInfo.timer_started_at).getTime()
      : null;

    function tick() {
      const estimatedServerNowMs = Date.now() - serverOffset;

      const runningSeconds =
        timerInfo.timer_is_running && runningStartedAtMs
          ? Math.floor((estimatedServerNowMs - runningStartedAtMs) / 1000)
          : 0;

      const elapsed = Math.max(
        0,
        baseAccumulatedSeconds + runningSeconds
      );

      setElapsedSeconds(elapsed);

      if (timerInfo.duration_minutes) {
        const totalSeconds = timerInfo.duration_minutes * 60;
        const remaining = Math.max(0, totalSeconds - elapsed);

        setRemainingSeconds(remaining);
        setTimerMode('countdown');

        if (remaining <= 0) {
          handleFinishExam(true);
        }
      } else {
        setRemainingSeconds(null);
        setTimerMode('stopwatch');
      }
    }

    tick();

    const intervalId = setInterval(tick, 1000);

    return () => clearInterval(intervalId);
  }, [timerInfo, handleFinishExam]);

  useEffect(() => {
    if (preguntasRef.current) {
      const navbarOffset = 112;
      const targetTop = preguntasRef.current.getBoundingClientRect().top + window.pageYOffset - navbarOffset;

      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    }

    // Actualizar página del navegador cuando cambiar de pregunta
    if (preguntas) {
      const firstQuestionIndex = preguntas.slice(0, bloquActual).reduce((acc, block) => acc + block.questions.length, 0);
      const pageIndex = Math.floor(firstQuestionIndex / 28);
      setPaginaNavegador(pageIndex);
    }
  }, [bloquActual, preguntas]);

  const handleSeleccionarRespuesta = async (questionId, optionId) => {
    setRespuestasSeleccionadas(prev => ({
      ...prev,
      [questionId]: optionId
    }));

    try {
      await saveExamAnswer({
        examId,
        questionId,
        selectedOptionId: optionId,
        timeSpentSeconds: null,
      });
    } catch (error) {
      showBackdropAlert({
        severity: "warning",
        title: "Respuesta no guardada",
        message: "No se pudo guardar la respuesta.",
        description: "Verifica tu conexión e inténtalo nuevamente.",
      });
    }
  };

  const handleMarcarPregunta = (questionId) => {
    setPreguntasMarcadas(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  function openReportDialog(block, question) {
    if (preguntasReportadas[question.question_id]) return;
    setReportTarget({ block, question });
    setReportComment('');
  }

  function closeReportDialog() {
    if (isSubmittingReport) return;
    setReportTarget(null);
    setReportComment('');
  }

  async function handleSubmitReport() {
    const question = reportTarget?.question;
    const block = reportTarget?.block;
    const trimmedComment = reportComment.trim();

    if (!question || !trimmedComment) return;

    setIsSubmittingReport(true);

    try {
      const selectedOptionId = respuestasSeleccionadas[question.question_id];
      const selectedOption = question.options.find((option) => option.id === selectedOptionId);

      await reportQuestion({
        examId,
        questionId: question.question_id,
        clinicalCase: block?.itemType === 'clinical_case' ? block.clinicalCase?.description : null,
        questionText: question.question_text,
        options: question.options.map((option) => `${option.position}. ${option.option_text}`),
        selectedOptionText: selectedOption ? `${selectedOption.position}. ${selectedOption.option_text}` : null,
        userComment: trimmedComment,
      });

      setPreguntasReportadas((prev) => ({ ...prev, [question.question_id]: true }));
      showBackdropAlert({
        severity: "success",
        title: "Reporte enviado",
        message: "Gracias por avisarnos.",
        description: "Revisaremos la pregunta lo antes posible.",
        primaryAction: { label: "Aceptar", onClick: closeBackdropAlert },
      });
    } catch (error) {
      showBackdropAlert({
        severity: "error",
        title: "No se pudo enviar",
        message: "No se pudo enviar el reporte.",
        description: "Intenta nuevamente en unos segundos.",
        primaryAction: { label: "Aceptar", onClick: closeBackdropAlert },
      });
    } finally {
      // La ventana de reporte se cierra siempre, exista o no error al enviar.
      setIsSubmittingReport(false);
      setReportTarget(null);
      setReportComment('');
    }
  }

  async function goToQuestion(position) {
  setBloquActual(position);

  try {
    await saveExamCurrentPosition(examId, position);
  } catch (error) {
    console.error(error);
  }
}

function getNextUnansweredBlock() {
  // Encuentra el siguiente bloque con preguntas sin responder
  for (let i = bloquActual + 1; i < preguntas.length; i++) {
    const hasUnanswered = preguntas[i].questions.some(
      q => !respuestasSeleccionadas[q.question_id]
    );
    if (hasUnanswered) {
      return i;
    }
  }
  // Si todas las preguntas siguientes están respondidas, va al último bloque
  return preguntas.length - 1;
}

useEffect(() => {
  if (!examId) return;

  async function resumeTimer() {
    try {
      const timer = await resumeExamTimer(examId);
      setTimerInfo(timer);
    } catch (error) {
      console.error('Error resuming timer:', error);
    }
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      // Fire-and-forget with keepalive so the pause request survives even if the tab closes.
      pauseExamTimerOnUnload(examId);
    }

    if (document.visibilityState === 'visible') {
      resumeTimer();
    }
  }

  function handlePageHide() {
    pauseExamTimerOnUnload(examId);
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', handlePageHide);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', handlePageHide);
  };
}, [examId]);

function formatTime(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function resolveBlockImage(block) {
  if (!block) return null;

  const questions = block.questions || [];
  const questionWithMedia = questions.find(
    (question) => question?.media?.some((media) => media?.media_type === 'image' && media?.base64_data)
  );
  const questionWithImageUrl = questions.find((question) => question?.image_url);
  const mediaImage = questionWithMedia?.media?.find(
    (media) => media?.media_type === 'image' && media?.base64_data
  );

  const imageUrl = questionWithImageUrl?.image_url || block.clinicalCase?.image_url || null;
  const imageAlt =
    mediaImage?.alt_text ||
    questionWithMedia?.image_alt_text ||
    questionWithImageUrl?.image_alt_text ||
    block.clinicalCase?.image_alt_text ||
    'Imagen del caso clinico';

  if (imageUrl) {
    return {
      src: imageUrl,
      alt: imageAlt,
    };
  }

  const rawBase64 = mediaImage?.base64_data || block.clinicalCase?.image_base64;

  if (typeof rawBase64 !== 'string' || !rawBase64.trim()) {
    return null;
  }

  const cleanBase64 = rawBase64.replace(/\s+/g, '');
  const mimeType = mediaImage?.mime_type || (cleanBase64.startsWith('PHN2Zy') ? 'image/svg+xml' : 'image/png');

  return {
    src: `data:${mimeType};base64,${cleanBase64}`,
    alt: imageAlt,
  };
}

  return (
    <>
      <div className="flex flex-wrap mt-100 items-stretch">
        <div ref={preguntasRef} className="w-full lg:w-8/12 px-4 bg-white shadow-md rounded-lg border border-gray-200 p-4 flex flex-col">
          <div className="w-full mb-4">
            <div className="flex items-center justify-between">
              {preguntas && preguntas.length > 0 && (
                <>
                  <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-start md:items-center w-full md:justify-between">
                    <h1 className="text-lg font-bold flex items-center whitespace-nowrap">
                      <ScrollText size={24} color="#6366f1" className="mr-2 flex-shrink-0" />Pregunta {preguntas.slice(0, bloquActual).reduce((acc, block) => acc + block.questions.length, 0) + 1} de {preguntas.reduce((acc, block) => acc + block.questions.length, 0)}
                    </h1>
                    <div className="flex w-full flex-col items-center gap-2 md:w-auto md:flex-row md:gap-4 md:items-center md:ml-auto md:flex-shrink-0">
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          width: { xs: '100%', md: 'auto' },
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          justifyContent: { xs: 'center', md: 'flex-start' },
                        }}
                      >
                        <Chip
                          label={`Dificultad: ${getDifficultyLabel(preguntas[bloquActual].questions[0]?.difficulty).label}`}
                          variant="outlined"
                          sx={{
                            padding: '8px 10px',
                            fontSize: '0.75rem',
                            color: getDifficultyLabel(preguntas[bloquActual].questions[0]?.difficulty).color,
                            borderColor: getDifficultyLabel(preguntas[bloquActual].questions[0]?.difficulty).border,
                            '& .MuiChip-label': {
                              color: getDifficultyLabel(preguntas[bloquActual].questions[0]?.difficulty).color,
                            },
                          }}
                        />
                        <Chip label={preguntas[bloquActual].questions[0]?.specialty_name || 'Ginecología'} color="secondary" variant="outlined" icon={<Stethoscope size={18} />} sx={{ padding: '8px 10px', fontSize: '0.75rem' }} />
                      </Stack>
                      <div className="self-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg px-3 py-2 border border-indigo-200 md:ml-8 lg:hidden">
                        <p className="text-xs font-semibold text-center text-gray-600">
                          {timerMode === 'stopwatch' ? 'Tiempo transcurrido' : 'Tiempo restante'}
                        </p>
                        <p
                          className={`text-sm font-bold text-center mt-0.5 ${
                            timerMode === 'countdown' && remainingSeconds !== null && remainingSeconds <= 300
                              ? 'text-red-600'
                              : 'text-indigo-600'
                          }`}
                        >
                          {timerMode === 'stopwatch'
                            ? formatTime(elapsedSeconds)
                            : formatTime(remainingSeconds || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="w-full mb-4">
            <div className="flex items-center justify-between">
              {isLoadingExam ? (
                <div className="flex min-h-[420px] w-full items-center justify-center p-4">
                  <div className="flex flex-col items-center gap-4 rounded-2xl bg-white px-8 py-10 shadow-sm">
                    <CircularProgress size={50} sx={{ color: '#6366f1' }} />
                    <p className="text-sm font-medium text-gray-600">Cargando examen...</p>
                  </div>
                </div>
              ) : preguntas && preguntas.length > 0 && (
                <>
                  <div className="w-full">
                    {(() => {
                      const block = preguntas[bloquActual];
                      const blockImage = resolveBlockImage(block);
                      const blockKey = block?.blockId || `block-${bloquActual}`;
                      const hideBrokenImage = Boolean(brokenImagesByBlock[blockKey]);
                      const imageAspectRatio = imageAspectRatioByBlock[blockKey] || null;
                      const isPortraitImage = Number(imageAspectRatio) > 0 && imageAspectRatio < 1;
                      return (
                        <div key={block.blockId} className="space-y-4">
                          {block.itemType === 'clinical_case' && (
                            <section className="rounded-2xl border bg-white p-5">
                              <h2 className="text-xl font-bold">
                                Caso clínico
                              </h2>

                              <div className="mt-3">
                                <div className="mb-4">
                                  <p className="whitespace-pre-line text-gray-700 leading-relaxed">
                                    {block.clinicalCase.description}
                                  </p>
                                </div>
                                {blockImage && !hideBrokenImage && (
                                  <div className="flex justify-center">
                                    <div className="inline-block max-w-full rounded-xl border-2 border-gray-300 bg-gray-50 p-2 shadow-md">
                                      <img
                                        src={blockImage.src}
                                        alt={blockImage.alt}
                                        onLoad={(event) => {
                                          const { naturalWidth, naturalHeight } = event.currentTarget;
                                          if (!naturalWidth || !naturalHeight) return;

                                          setImageAspectRatioByBlock((prev) => ({
                                            ...prev,
                                            [blockKey]: naturalWidth / naturalHeight,
                                          }));
                                        }}
                                        onError={() => {
                                          setBrokenImagesByBlock((prev) => ({
                                            ...prev,
                                            [blockKey]: true,
                                          }));
                                        }}
                                        loading="lazy"
                                        decoding="async"
                                        style={{
                                          width: isPortraitImage ? 'auto' : '100%',
                                          height: 'auto',
                                          maxWidth: '100%',
                                          maxHeight: '72vh',
                                          objectFit: 'contain'
                                        }}
                                        className="block max-w-full rounded-lg"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>

                              {block.clinicalCase.labs && Object.keys(block.clinicalCase.labs).length > 0 && (
                                <div className="mt-5">
                                  <h3 className="text-md font-semibold text-gray-800 mb-3">Laboratorios</h3>
                                  <div className="overflow-x-auto">
                                    <table className="w-full border-collapse text-sm">
                                      <thead>
                                        <tr>
                                          {Object.entries(block.clinicalCase.labs).map(([key]) => (
                                            <th key={key} className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-700" style={{ backgroundColor: '#e5e7eb' }}>{key}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          {Object.entries(block.clinicalCase.labs).map(([key, value]) => (
                                            <td key={key} className="border border-gray-300 px-4 py-2 text-gray-600">{value}</td>
                                          ))}
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </section>
                          )}

                          {block.questions.map((question) => (
                            <section
                              key={question.question_id}
                              className="relative rounded-2xl border bg-white p-3 lg:p-5"
                            >
                              <div style={{ position: 'absolute', top: 16, left: 16, right: 16 }} className="flex items-center justify-between">
                                <button
                                  onClick={() => openReportDialog(block, question)}
                                  disabled={Boolean(preguntasReportadas[question.question_id])}
                                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                  title={preguntasReportadas[question.question_id] ? "Pregunta reportada" : "Reportar pregunta"}
                                >
                                  <Flag
                                    size={22}
                                    className={
                                      preguntasReportadas[question.question_id]
                                        ? 'text-red-500 fill-current'
                                        : 'text-gray-400 hover:text-red-500'
                                    }
                                  />
                                  <span>Reportar pregunta</span>
                                </button>
                                <button
                                  onClick={() => handleMarcarPregunta(question.question_id)}
                                  className="p-1 hover:bg-yellow-50 rounded transition-colors"
                                  title="Marcar pregunta"
                                >
                                  <svg
                                    className={`w-6 h-6 ${
                                      preguntasMarcadas[question.question_id]
                                        ? 'text-yellow-500 fill-current'
                                        : 'text-gray-400'
                                    }`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                </button>
                              </div>

                              <p className="pt-10 pr-2 font-semibold leading-relaxed mb-2">
                                {question.question_text}
                              </p>

                              <div className="mt-4 space-y-2">
                                {question.options.map((option) => {
                                  const isSelected = respuestasSeleccionadas[question.question_id] === option.id;
                                  const isHovered = hoverOption === `${question.question_id}-${option.id}`;
                                  const shouldHighlight = isSelected || isHovered;

                                  return (
                                    <button
                                      key={option.id}
                                      type="button"
                                      onClick={() => handleSeleccionarRespuesta(question.question_id, option.id)}
                                      onMouseEnter={() => setHoverOption(`${question.question_id}-${option.id}`)}
                                      onMouseLeave={() => setHoverOption(null)}
                                      style={
                                        shouldHighlight
                                          ? {
                                              border: '2px solid #6366f1',
                                              backgroundColor: '#f0f4ff',
                                              outline: 'none',
                                              boxShadow: 'none'
                                            }
                                          : {
                                              border: '1px solid #d1d5db',
                                              backgroundColor: 'white',
                                              outline: 'none',
                                              boxShadow: 'none'
                                            }
                                      }
                                      className="block w-full rounded-xl p-3 text-left transition-all focus:outline-none"
                                    >
                                      {option.position}. {option.option_text}
                                    </button>
                                  );
                                })}
                              </div>
                            </section>
                          ))}

                          <div className="flex justify-end gap-3 mt-6">
                            {bloquActual > 0 && (
                              <Button 
                                variant="outlined"
                                onClick={() => goToQuestion(bloquActual - 1)}
                                startIcon={<ArrowLeft size={20} />}
                                sx={{ color: '#6366f1', borderColor: '#6366f1', marginRight: 'auto' }}
                              >
                                Anterior
                              </Button>
                            )}
                            {bloquActual < preguntas.length - 1 && (
                              <Button 
                                variant="contained"
                                endIcon={<ArrowRight size={20} />}
                                onClick={() => goToQuestion(getNextUnansweredBlock())}
                                disabled={!preguntas[bloquActual].questions.every(q => respuestasSeleccionadas[q.question_id])}
                                sx={{ backgroundColor: '#6366f1' }}
                              >
                                Siguiente
                              </Button>
                            )}
                            {bloquActual === preguntas.length - 1 && (
                              <Button 
                                variant="contained"
                                onClick={() => handleFinishExam(false)}
                                disabled={!preguntas[bloquActual].questions.every(q => respuestasSeleccionadas[q.question_id]) || isFinishing}
                                sx={{ backgroundColor: '#6366f1' }}
                              >
                                Finalizar Examen
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="w-full lg:w-4/12 px-0 lg:px-4 lg:pl-6">
          <div className="hidden lg:block bg-white shadow-md rounded-lg border border-gray-200 p-4 mb-2">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-2">
                <svg className="w-6 h-6s text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 2m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-600">{timerMode === 'stopwatch' ? 'Tiempo transcurrido' : 'Tiempo restante'}</p>
                <p
                  className={`text-2xl font-bold ${
                    timerMode === 'countdown' && remainingSeconds !== null && remainingSeconds <= 300
                      ? 'text-red-600'
                      : 'text-indigo-600'
                  }`}
                >
                  {timerMode === 'stopwatch'
                    ? formatTime(elapsedSeconds)
                    : formatTime(remainingSeconds || 0)}
                </p>
                <p className="text-xs text-gray-500">
                  {timerMode === 'stopwatch'
                    ? 'Sin límite de tiempo'
                    : `de ${timerInfo?.duration_minutes ? `${String(timerInfo.duration_minutes).padStart(2, '0')}:00:00 hrs` : 'Ilimitado'}`}
                </p>
              </div>
            </div>
          </div>

          <div className="hidden lg:block bg-white shadow-md rounded-lg border border-gray-200 p-3 mb-4">
            <h3 className="text-base font-bold mb-2 text-gray-800">Resumen rápido</h3>

            <div className="flex justify-between items-center gap-2">
              <div className="flex flex-col items-center gap-0.5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#86efac' }}>
                  <svg className="w-5 h-5 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-xs text-gray-600">Respondidas</p>
                <p className="text-base font-bold text-gray-500">
                  {preguntas ? preguntas.reduce((acc, block) => acc + block.questions.filter(q => respuestasSeleccionadas[q.question_id]).length, 0) : 0}
                </p>
              </div>

              <div className="flex flex-col items-center gap-0.5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#e5e7eb' }}>
                  <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-xs text-gray-600">Sin responder</p>
                <p className="text-base font-bold text-gray-500">
                  {preguntas ? preguntas.reduce((acc, block) => acc + block.questions.filter(q => !respuestasSeleccionadas[q.question_id]).length, 0) : 0}
                </p>
              </div>

              <div className="flex flex-col items-center gap-0.5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#eef2ff' }}>
                  <p className="text-xs font-bold text-indigo-600">%</p>
                </div>
                <p className="text-xs text-gray-600">Progreso</p>
                <p className="text-base font-bold text-indigo-600">
                  {preguntas ? Math.round((preguntas.reduce((acc, block) => acc + block.questions.filter(q => respuestasSeleccionadas[q.question_id]).length, 0) / preguntas.reduce((acc, block) => acc + block.questions.length, 0)) * 100) : 0}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white shadow-md rounded-lg border border-gray-200 p-4 mb-4 mt-4 lg:mt-0">
            <h3 className="text-lg font-bold mb-3 text-gray-800">Navegador de preguntas</h3>

            <div className="flex flex-wrap gap-x-2 gap-y-1 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: '#86efac' }}></div>
                <span className="text-xs text-gray-600 mr-2">Respondida</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: '#fcd34d' }}></div>
                <span className="text-xs text-gray-600 mr-2">Marcada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: '#e5e7eb' }}></div>
                <span className="text-xs text-gray-600">Sin responder</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <tbody>
                  {preguntas && (() => {
                    // Encontrar la primera pregunta no respondida globalmente
                    let firstUnansweredGlobalIndex = null;
                    let questionCounter = 1;
                    for (let i = 0; i < preguntas.length; i++) {
                      for (let j = 0; j < preguntas[i].questions.length; j++) {
                        if (!respuestasSeleccionadas[preguntas[i].questions[j].question_id]) {
                          firstUnansweredGlobalIndex = questionCounter;
                          break;
                        }
                        questionCounter++;
                      }
                      if (firstUnansweredGlobalIndex !== null) break;
                    }

                    return Array.from({ length: 4 }).map((_, rowIndex) => {
                      const actualRowIndex = paginaNavegador * 4 + rowIndex;
                      return (
                        <tr key={`row-${actualRowIndex}`} className="flex gap-2 mb-2">
                          {Array.from({ length: 7 }).map((_, colIndex) => {
                            const globalIndex = actualRowIndex * 7 + colIndex + 1;
                            const totalQuestions = preguntas.reduce((acc, b) => acc + b.questions.length, 0);

                            if (globalIndex > totalQuestions) {
                              return <td key={`empty-${colIndex}`} className="flex-1"></td>;
                            }

                            let blockIndex = 0;
                            let questionCount = 0;
                            let found = false;

                            for (let i = 0; i < preguntas.length; i++) {
                              if (questionCount + preguntas[i].questions.length >= globalIndex) {
                                blockIndex = i;
                                found = true;
                                break;
                              }
                              questionCount += preguntas[i].questions.length;
                            }

                            if (!found) return null;

                            const question = preguntas[blockIndex]?.questions[globalIndex - questionCount - 1];
                            const isAnswered = question && respuestasSeleccionadas[question.question_id];
                            const isMarked = question && preguntasMarcadas[question.question_id];
                            const isCurrent = blockIndex === bloquActual;
                            const isFirstUnanswered = globalIndex === firstUnansweredGlobalIndex;

                            let bgColor = '#e5e7eb';
                            if (isAnswered) {
                              bgColor = '#86efac';
                            }
                            if (isMarked) {
                              bgColor = '#fcd34d';
                            }

                            return (
                              <td key={`${actualRowIndex}-${colIndex}`} className="flex-1">
                                <button
                                  onClick={() => goToQuestion(blockIndex)}
                                  disabled={!isAnswered && !isFirstUnanswered}
                                  className="w-full aspect-square rounded-2xl font-bold text-sm transition-all flex items-center justify-center border-2 disabled:cursor-not-allowed disabled:opacity-50"
                                  style={{
                                    backgroundColor: isCurrent ? '#eef2ff' : bgColor,
                                    color: isCurrent ? '#4f46e5' : '#1f2937',
                                    borderColor: isCurrent ? '#4f46e5' : '#e5e7eb'
                                  }}
                                >
                                  {globalIndex}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>

            {preguntas && Math.ceil(preguntas.reduce((acc, b) => acc + b.questions.length, 0) / 28) > 1 && (
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => setPaginaNavegador(Math.max(0, paginaNavegador - 1))}
                  disabled={paginaNavegador === 0}
                  className="px-3 py-1 text-sm bg-indigo-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="text-xs text-gray-600">
                  Página {paginaNavegador + 1} de {Math.ceil(preguntas.reduce((acc, b) => acc + b.questions.length, 0) / 28)}
                </span>
                <button
                  onClick={() => setPaginaNavegador(Math.min(Math.ceil(preguntas.reduce((acc, b) => acc + b.questions.length, 0) / 28) - 1, paginaNavegador + 1))}
                  disabled={paginaNavegador === Math.ceil(preguntas.reduce((acc, b) => acc + b.questions.length, 0) / 28) - 1}
                  className="px-3 py-1 text-sm bg-indigo-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <BackdropAlert
        open={backdropAlert.open}
        onClose={closeBackdropAlert}
        severity={backdropAlert.severity}
        title={backdropAlert.title}
        message={backdropAlert.message}
        description={backdropAlert.description}
        closeOnBackdropClick={backdropAlert.closeOnBackdropClick}
        primaryAction={
          backdropAlert.primaryAction || {
            label: "Aceptar",
            onClick: closeBackdropAlert,
          }
        }
        secondaryAction={backdropAlert.secondaryAction}
      />

      <Dialog open={Boolean(reportTarget)} onClose={closeReportDialog} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 4 } } }}>
        <DialogTitle>Reportar pregunta</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-600 mb-3">
            Cuéntanos qué está mal redactado o incorrecto en esta pregunta. Enviaremos el detalle a soporte.
          </p>
          <TextField
            autoFocus
            multiline
            minRows={4}
            fullWidth
            placeholder="Describe el problema..."
            value={reportComment}
            onChange={(event) => setReportComment(event.target.value)}
            disabled={isSubmittingReport}
            sx={{
              // Tailwind's forms reset styles the raw textarea with its own border/ring, doubling MUI's outline.
              '& .MuiInputBase-input, & .MuiInputBase-input:focus': {
                border: 'none',
                boxShadow: 'none',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeReportDialog} disabled={isSubmittingReport}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitReport}
            disabled={isSubmittingReport || !reportComment.trim()}
            sx={{ backgroundColor: '#6366f1' }}
          >
            {isSubmittingReport ? 'Enviando...' : 'Enviar reporte'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
